import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit3, Trash2, Languages } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AdminForm from "@/components/AdminForm";

const NEW_LABELS = { courses: "course", prompts: "prompt", blog: "post", news: "news item", lessons: "lesson" };

export default function AdminActions({ kind, item, onChange, mode = "auto", redirectAfterDelete = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState(null);
  const [translating, setTranslating] = useState(false);

  if (user?.role !== "admin") return null;

  const isEdit = Boolean(item?.id);
  const showAsRow = mode === "row";
  const canTranslate = isEdit && kind !== "courses";

  const openNew = () => { setFormInitial(null); setFormOpen(true); };
  const openEdit = () => { setFormInitial(item); setFormOpen(true); };

  const translateBn = async () => {
    setTranslating(true);
    try {
      await api.post(`/admin/translate/${kind}/${item.id}`);
      toast.success("Bangla version saved");
      if (onChange) onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Translate failed");
    } finally {
      setTranslating(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/${kind}/${item.id}`);
      toast.success("Deleted");
      if (onChange) onChange();
      if (redirectAfterDelete) navigate(redirectAfterDelete);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const onSaved = () => {
    if (onChange) onChange();
    setFormOpen(false);
  };

  if (showAsRow) {
    return (
      <>
        <span className="inline-flex items-center gap-1" onClick={(e) => e.preventDefault()}>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(); }}
            className="p-2 rounded-full bg-white/90 backdrop-blur border border-slate-200 text-slate-700 hover:text-orange-500 hover:border-orange-400 transition-colors"
            title="Edit"
            data-testid={`row-edit-${kind}-${item?.id}`}
          >
            <Edit3 size={14} />
          </button>
          {canTranslate && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); translateBn(); }}
              disabled={translating}
              className="p-2 rounded-full bg-white/90 backdrop-blur border border-slate-200 text-slate-700 hover:text-orange-500 hover:border-orange-400 transition-colors disabled:opacity-50"
              title="Translate to Bangla"
              data-testid={`row-translate-${kind}-${item?.id}`}
            >
              <Languages size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); del(); }}
            className="p-2 rounded-full bg-white/90 backdrop-blur border border-white/10 text-red-400 hover:text-red-300 hover:border-red-400/50 transition-colors"
            title="Delete"
            data-testid={`row-delete-${kind}-${item?.id}`}
          >
            <Trash2 size={14} />
          </button>
        </span>
        {formOpen && (
          <AdminForm
            kind={kind}
            initial={formInitial}
            onClose={() => setFormOpen(false)}
            onSaved={onSaved}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="inline-flex items-center gap-2" data-testid={`admin-actions-${kind}`}>
        {!isEdit && (
          <button
            type="button"
            onClick={openNew}
            className="btn-glow !py-2 !px-4 !text-xs"
            data-testid={`new-${kind}-btn`}
          >
            <Plus size={14} /> New {NEW_LABELS[kind] || kind}
          </button>
        )}
        {isEdit && (
          <>
            <button
              type="button"
              onClick={openEdit}
              className="btn-glass !py-2 !px-4 !text-xs"
              data-testid={`edit-${kind}-btn`}
            >
              <Edit3 size={14} /> Edit
            </button>
            {canTranslate && (
              <button
                type="button"
                onClick={translateBn}
                disabled={translating}
                className="btn-glass !py-2 !px-4 !text-xs disabled:opacity-50"
                data-testid={`translate-${kind}-btn`}
              >
                <Languages size={14} /> {translating ? "Translating…" : (item?.title_bn ? "Re-translate BN" : "Translate BN")}
              </button>
            )}
            <button
              type="button"
              onClick={del}
              className="btn-glass !py-2 !px-4 !text-xs hover:!border-red-400/50 hover:!text-red-400"
              data-testid={`delete-${kind}-btn`}
            >
              <Trash2 size={14} /> Delete
            </button>
          </>
        )}
      </div>
      {formOpen && (
        <AdminForm
          kind={kind}
          initial={formInitial}
          onClose={() => setFormOpen(false)}
          onSaved={onSaved}
        />
      )}
    </>
  );
}
